import { useMemo, forwardRef } from "react";
import PropTypes from "prop-types";

import { strCode } from "../../consts";
import { t } from "../../services";

import {
  WrapperContainer,
  FlexContainers,
  Paragraph2,
  Checkbox,
  TextOverflowWrapper
} from "../../components";
import { DropdownOptionsList, OptionButton, OptionTextWrapper } from "./styled";

const DropdownOptions = forwardRef(
  (
    {
      visible,
      options,
      selectedOptions,
      onSelectOption,
      showCheckboxes,
      searchText,
      activeOptionIndex,
      viewOnlyOptionIds,
      isBlock,
      showAtTop
    },
    optionsListRef
  ) => {
    const selectedOptionIds = useMemo(() => {
      return selectedOptions?.map((option) => option?.id);
    }, [selectedOptions]);

    const isViewOnlyOption = (id) => {
      if (viewOnlyOptionIds) {
        return viewOnlyOptionIds?.includes(id);
      }

      return false;
    };

    return (
      <DropdownOptionsList
        visible={visible}
        isBlock={isBlock}
        showScroll={options?.length >= 4}
        showAtTop={showAtTop}
        ref={optionsListRef}>
        {options?.length === 0 ? (
          <WrapperContainer
            Container={FlexContainers.FlexRowCenter}
            height={"100%"}
            minHeight="100px"
            width="100%">
            <Paragraph2>{t(strCode.NO_OPTIONS_AVAILABLE)}</Paragraph2>
          </WrapperContainer>
        ) : (
          options.map((option, i) => {
            const selected = selectedOptionIds.includes(option.id);
            return (
              <OptionButton
                key={option.id}
                onClick={() => {
                  onSelectOption(option.id);
                }}
                disabled={isViewOnlyOption(option.id)}
                showCheckboxes={showCheckboxes}
                active={i === activeOptionIndex}
                selected={selected}>
                {showCheckboxes && (
                  <WrapperContainer mr={16}>
                    <Checkbox
                      checked={selected}
                      onCheck={(e) => {
                        e.stopPropagation();
                        onSelectOption(option.id);
                      }}
                      disabled={isViewOnlyOption(option.id)}
                    />
                  </WrapperContainer>
                )}

                {option.component ? (
                  option.component
                ) : (
                  <OptionTextWrapper Container={FlexContainers.FlexRowStart}>
                    <TextOverflowWrapper highlightedText={!selected && searchText}>
                      {t(option.value)}
                    </TextOverflowWrapper>
                  </OptionTextWrapper>
                )}
              </OptionButton>
            );
          })
        )}
      </DropdownOptionsList>
    );
  }
);

DropdownOptions.displayName = "DropdownOptions";

DropdownOptions.propTypes = {
  visible: PropTypes.bool.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired,
      component: PropTypes.node
    })
  ).isRequired,
  selectedOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  onSelectOption: PropTypes.func.isRequired,
  showCheckboxes: PropTypes.bool,
  searchText: PropTypes.string,
  viewOnlyOptionIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  ),
  isBlock: PropTypes.bool,
  showAtTop: PropTypes.bool,
  activeOptionIndex: PropTypes.number
};

export default DropdownOptions;
